import PermissionButton from "../../components/ui/PermissionButton";
import FinancialScopeNotice from '../../components/ui/FinancialScopeNotice';
import TableActions from '../../components/ui/TableActions';
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import AddIncomeModal from "./AddIncomeModal";
import IncomeDetailsModal from "./IncomeDetailsModal";

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { Plus, Search, Download, Eye, Pencil } from "lucide-react";

const COLORS = [
  "#facc15",
  "#22c55e",
  "#3b82f6",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
];

export default function Income() {
  const [incomeData, setIncomeData] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    today: 0,
    pending: 0,
    categories: 0,
  });

  const [accounts, setAccounts] = useState([]);

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");

  const [showAdd, setShowAdd] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(null);

  useEffect(() => {
    loadIncome();
    loadAccounts();
  }, []);

  async function loadIncome() {
    try {
      const res = await fetch("/api/income", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setIncomeData(data.incomes || []);

        setSummary({
          total: Number(data.summary?.total || 0),
          today: Number(data.summary?.today || 0),
          pending: Number(data.summary?.pending || 0),
          categories: Number(data.summary?.categories || 0),
        });
      }
    } catch (err) {
      console.log(err);
    }
  }

  async function loadAccounts() {
    try {
      const res = await fetch("/api/income/accounts/list", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setAccounts(data.accounts);
      }
    } catch (err) {
      console.log(err);
    }
  }

  const filtered = useMemo(() => {
    return incomeData.filter((item) => {
      const matchesSearch =
        (item.income_no || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.source || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.reference || "").toLowerCase().includes(search.toLowerCase());

      const matchesCategory =
        category === "All" || item.account_name === category;

      return matchesSearch && matchesCategory;
    });
  }, [incomeData, search, category]);

  const chartData = useMemo(() => {
    const map = {};

    filtered.forEach((item) => {
      const key = item.account_name || "Other";

      map[key] = (map[key] || 0) + Number(item.amount);
    });

    return Object.entries(map).map(([name, value]) => ({
      name,
      value,
    }));
  }, [filtered]);

  const refresh = () => {
    loadIncome();
    setSelectedIncome(null);
    setShowAdd(false);
  };

  return (
    <DashboardLayout>
      <FinancialScopeNotice/>
      <div className="space-y-10">
        {/* Header */}

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-serif">Income Management</h1>
            <p className="text-sm text-gray-400 mt-1">
              Track all business income & receipts
            </p>
          </div>

          <PermissionButton permissionModule="income"
            onClick={() => {
              setSelectedIncome(null);
              setShowAdd(true);
            }}
            className="flex items-center gap-2 bg-amber-400 text-black px-5 py-2 rounded-xl font-semibold"
          >
            <Plus size={18} />
            Add Income
          </PermissionButton>
        </div>

        {/* KPI */}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <KpiCard
            title="Total Income"
            value={`₹${summary.total.toLocaleString("en-IN")}`}
          />

          <KpiCard
            title="Today's Income"
            value={`₹${summary.today.toLocaleString("en-IN")}`}
          />

          <KpiCard
            title="Pending Receipts"
            value={`₹${summary.pending.toLocaleString("en-IN")}`}
            danger
          />

          <KpiCard title="Income Categories" value={summary.categories} />
        </div>

        {/* Filters */}

        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-3 text-gray-500"
              />

              <input
                placeholder="Search income..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 rounded-xl bg-black/40 border border-white/10"
              />
            </div>

            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-4 py-2 rounded-xl bg-black/40 border border-white/10"
            >
              <option value="All">All</option>

              {accounts.map((acc) => (
                <option key={acc.id} value={acc.name}>
                  {acc.name}
                </option>
              ))}
            </select>

            <PermissionButton permissionModule="income" className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 hover:bg-white/5">
              <Download size={16} />
              Export
            </PermissionButton>
          </div>
        </div>

        {/* Charts */}

        <div className="grid xl:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-5">Income by Account</h3>

            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie data={chartData} dataKey="value" outerRadius={110} label>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>

                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-5">Income Overview</h3>

            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData}>
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />

                <XAxis dataKey="name" />

                <YAxis />

                <Tooltip />

                <Bar dataKey="value" fill="#facc15" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table */}

        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-black/30 text-gray-400">
              <tr>
                <th className="text-left p-4">Income No</th>
                <th className="text-left p-4">Date</th>
                <th className="text-left p-4">Account</th>
                <th className="text-left p-4">Source</th>
                <th className="text-left p-4">Payment</th>
                <th className="text-left p-4">Reference</th>
                <th className="text-right p-4">Amount</th>
                <th className="text-center p-4">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((income) => (
                <tr
                  key={income.id}
                  className="border-t border-white/10 hover:bg-white/5 transition"
                >
                  <td className="p-4 font-medium">{income.income_no}</td>

                  <td className="p-4">{income.income_date}</td>

                  <td className="p-4">{income.account_name}</td>

                  <td className="p-4">{income.source}</td>

                  <td className="p-4">{income.payment_mode}</td>

                  <td className="p-4">{income.reference || "-"}</td>

                  <td className="p-4 text-right font-semibold text-emerald-400">
                    ₹{Number(income.amount).toLocaleString("en-IN")}
                  </td>

                  <td className="p-4"><TableActions>
                    <div className="flex justify-center gap-3">
                      <PermissionButton permissionModule="income"
                        onClick={() => setSelectedIncome(income)}
                        className="p-2 rounded-lg hover:bg-white/10"
                      >
                        <Eye size={17} />
                      </PermissionButton>

                      <PermissionButton permissionModule="income"
                        onClick={() => {
                          setSelectedIncome(income);
                          setShowAdd(true);
                        }}
                        className="p-2 rounded-lg hover:bg-white/10"
                      >
                        <Pencil size={17} />
                      </PermissionButton>
                    </div>
                  </TableActions></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <AddIncomeModal
          income={selectedIncome}
          accounts={accounts}
          onSuccess={refresh}
          onClose={() => {
            setShowAdd(false);
            setSelectedIncome(null);
          }}
        />
      )}

      {selectedIncome && !showAdd && (
        <IncomeDetailsModal
          income={selectedIncome}
          onClose={() => setSelectedIncome(null)}
        />
      )}
    </DashboardLayout>
  );
}

function KpiCard({ title, value, danger = false }) {
  return (
    <div
      className={`rounded-2xl p-6 border transition ${
        danger
          ? "bg-red-500/10 border-red-500/30"
          : "bg-white/5 border-white/10 hover:border-amber-400/40"
      }`}
    >
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>

      <h2
        className={`mt-3 text-3xl font-serif ${
          danger ? "text-red-400" : "text-white"
        }`}
      >
        {value}
      </h2>
    </div>
  );
}
